import type { SimulationState, Wave } from '../sim/index.js';
import { WaveState, isWaveVisible } from '../sim/index.js';
import {
  createGameplayWaveState,
  gameplayWaveSeed,
  type GameplayWaveOutcome,
  type GameplayWaveState,
  type GameplayWaveStyle,
  type Mutable,
} from '../visual/gameplay/GameplayWave.js';
import type { Rgb } from '../visual/palette/VisualPalette.js';
import type { GameplayWaveField } from './gameplay/GameplayWaveField.js';
import type { ChartInstanceData, RippleField } from './wave/RippleField.js';
import { MAX_TEXT_RINGS } from './wave/RippleField.js';
import { gradeCode } from '../sim/index.js';

/**
 * Copies simulation waves into the renderer's instance buffers, and decides how
 * each one presents itself as a seal.
 *
 * The one rule this file exists to enforce: **the arrow points one way**. It
 * reads wave records and writes GPU attributes. It never mutates a wave, never
 * calls back into the simulation, and never keeps a reference to a record beyond
 * the call. That is the whole reason the simulation can be tested without a
 * renderer and re-run deterministically.
 *
 * The mapping is small but not trivial, because the chart speaks a different
 * dialect from the shader: degrees against radians, full gate widths against
 * half-widths, and a warning phase that has to read as *something* even though
 * its radius is zero.
 */

const DEGREES_TO_RADIANS = Math.PI / 180;

/** Substituted when a chart leaves `gateColor` at Unity's zeroed default. */
const DEFAULT_GATE_COLOR = { r: 0.35, g: 1, b: 0.95 } as const;

/** Warm gold, so a note word separates from the cyan/violet the seals live in. */
const DEFAULT_NOTE_COLOR = { r: 1, g: 0.86, b: 0.42 } as const;

/**
 * Chooses which word on a wave's ring is the note, or a negative number for
 * none. Given the whole wave so a future implementation can key off the chart
 * rather than only the index.
 */
export type NoteWordSelector = (wave: Wave, wordsPerRing: number) => number;

/** The default: no wave carries a note, because judgement does not exist yet. */
const NO_NOTE: NoteWordSelector = () => -1;

/**
 * Which style a wave should be drawn in.
 *
 * A function rather than a setting, because the answer belongs to
 * `GameplayWaveDirector` and this file has no business knowing how it was
 * decided. The default answers MANDALA to everything, which is the behaviour
 * that existed before styles did — so a binder nobody configured draws exactly
 * what it always drew.
 */
export type GameplayStyleResolver = (wave: Wave) => GameplayWaveStyle;

/**
 * Which colour a wave paints with.
 *
 * A second function rather than a field on the style resolver's result, because
 * the two answers come from different streams — see
 * `GameplayWaveDirector.pigmentFor`. Optional: a binder with no pigment rule
 * falls back to the wave's own authored colour.
 */
export type GameplayPigmentResolver = (wave: Wave) => Rgb;

const ALL_MANDALA: GameplayStyleResolver = () => 'MANDALA';

export interface WaveViewBinderOptions {
  /**
   * Seconds of fade as a wave approaches its despawn radius.
   *
   * Unity released a wave the instant it crossed the threshold, which pops. The
   * fade is presentation only: the simulation still retires on the exact radius,
   * so nothing about timing or judgement changes.
   */
  readonly fadeOutRadiusFraction?: number;
  /** Brightness of a wave during its pre-expansion warning window. */
  readonly warningLife?: number;
  // Rotation speeds are per ring and live in `TEXT_RING_LAYOUT`; there is no
  // single inscription rate to override any more.
  /** Picks the note word per wave. Defaults to "no wave has one". */
  readonly noteWordSelector?: NoteWordSelector;
  /** Colour, brightness and pulse depth of a note word. */
  readonly noteColor?: { readonly r: number; readonly g: number; readonly b: number };
  readonly noteGain?: number;
  readonly notePulseDepth?: number;
  /**
   * How far from the best moment the note word starts to swell, in seconds.
   *
   * Wider than the PERFECT window on purpose: the point is to be seen
   * *approaching*, not to confirm something already missed.
   */
  readonly notePulseWindow?: number;
  /** How long a failed tap glitches the seal, in seconds. */
  readonly badFlashSeconds?: number;
  /** How far the timing arc travels before reaching its reference position. */
  readonly arcSweep?: number;
  readonly arcPulseRate?: number;
  /** Must match the simulation's, or the dissolve will clip or hang. */
  readonly resolveFadeSeconds?: number;
}

/**
 * Where the timing arc sits at the best moment: straight up.
 *
 * Fixed rather than seeded. The direction of travel varies per wave so the
 * field does not look mechanical, but the *destination* has to be the same on
 * every seal or there is nothing for the player to learn.
 */
const ARC_REFERENCE_ANGLE = Math.PI * 0.5;

export class WaveViewBinder {
  /** Where non-mandala styles are drawn. Absent until one is installed. */
  private gameplayField: GameplayWaveField | null = null;
  private styleResolver: GameplayStyleResolver = ALL_MANDALA;
  private pigmentResolver: GameplayPigmentResolver | null = null;
  /** Reused every frame, so a sync allocates nothing. */
  private readonly gameplayScratch: Mutable<GameplayWaveState> = createGameplayWaveState();
  /** Waves a style pool could not take, for diagnostics. */
  private droppedGameplay = 0;

  private readonly fadeFraction: number;
  private readonly warningLife: number;
  private readonly noteColor: { readonly r: number; readonly g: number; readonly b: number };
  private readonly noteGain: number;
  private readonly notePulseDepth: number;
  private readonly notePulseWindow: number;
  private readonly badFlashSeconds: number;
  private readonly arcSweep: number;
  private readonly arcPulseRate: number;
  private readonly resolveFadeSeconds: number;
  private noteWordSelector: NoteWordSelector;
  private lastWritten = 0;
  /** The song time of the frame being written; set once per `sync`. */
  private songTime = 0;

  /** Reused so a frame of 26 waves allocates nothing. */
  private readonly scratch: ChartInstanceData = {
    centerX: 0, centerY: 0, radius: 0, gateAngle: 0, gateHalfWidth: 0, life: 0,
    red: 1, green: 1, blue: 1, gateRed: 1, gateGreen: 1, gateBlue: 1,
    spinOuter: 0, spinInner: 0, pattern: 0, state: 1,
    noteWordIndex: -1, noteRed: 1, noteGreen: 1, noteBlue: 1,
    noteGain: 1, notePulse: 0,
    spinRing0: 0, spinRing1: 0, spinRing2: 0, spinRing4: 0,
    timingOffset: 0, arcAngle: 0, resolveProgress: 0, gradeCode: -1, badFlash: 0,
    visualLead: 1,
  };

  /** Scratch for the per-ring angles, so a frame of 26 waves allocates nothing. */
  private readonly spins = new Float64Array(MAX_TEXT_RINGS);

  constructor(
    private readonly field: RippleField,
    options: WaveViewBinderOptions = {},
  ) {
    this.fadeFraction = options.fadeOutRadiusFraction ?? 0.18;
    this.warningLife = options.warningLife ?? 0.55;
    this.noteWordSelector = options.noteWordSelector ?? NO_NOTE;
    this.noteColor = options.noteColor ?? DEFAULT_NOTE_COLOR;
    // Enough to separate the note from its neighbours, and no more: at higher
    // gains the word crosses the bloom threshold hard and turns into a white
    // blob, which is the opposite of what a word the player must read needs.
    this.noteGain = options.noteGain ?? 1.35;
    this.notePulseDepth = options.notePulseDepth ?? 0.33;
    this.notePulseWindow = options.notePulseWindow ?? 0.55;
    this.badFlashSeconds = options.badFlashSeconds ?? 0.18;
    // Three quarters of a turn. A full turn would put the arc back where it
    // started at the best moment, which reads as "nothing happened".
    this.arcSweep = options.arcSweep ?? Math.PI * 1.5;
    this.arcPulseRate = options.arcPulseRate ?? 2.2;
    this.resolveFadeSeconds = options.resolveFadeSeconds ?? 0.45;
  }

  /**
   * Installs the rule that decides which word is the note.
   *
   * Deliberately a seam rather than an implementation: the judgement phase owns
   * the question of *which* word a wave's note is, and the view's only job is to
   * light whichever one it is told about. Swapping this function changes nothing
   * about spawn timing, radius, gate geometry or retirement.
   */
  setNoteWordSelector(selector: NoteWordSelector | null): void {
    this.noteWordSelector = selector ?? NO_NOTE;
  }

  /**
   * Installs the field that draws non-mandala styles, and the rule that chooses.
   *
   * Both optional and both independently removable: passing null for either
   * puts every wave back on the mandala path, which is the regression switch
   * §36 asks for and is worth being able to throw without editing anything.
   */
  setGameplayVisual(
    field: GameplayWaveField | null,
    resolver: GameplayStyleResolver | null,
    pigment: GameplayPigmentResolver | null = null,
  ): void {
    this.gameplayField = field;
    this.styleResolver = resolver ?? ALL_MANDALA;
    this.pigmentResolver = pigment;
  }

  /** How many waves a style pool refused last frame. */
  get droppedGameplayWaves(): number {
    return this.droppedGameplay;
  }

  /**
   * Reflects the current simulation state into the ripple field.
   *
   * Walks the wave pool in order and packs live waves into consecutive instance
   * slots, so the GPU never iterates holes left by retired waves.
   */
  sync(state: SimulationState): void {
    // Driven by song time, not wall time, so the breathing is reproducible and
    // cannot drift away from the music across a pause or a tab restore.
    this.songTime = state.songTime;
    this.field.setArcPulse(0.5 + 0.5 * Math.sin(state.songTime * Math.PI * 2 * this.arcPulseRate));

    const gameplay = this.gameplayField;
    if (gameplay !== null) {
      gameplay.begin();
      gameplay.setTime(state.songTime);
    }
    this.droppedGameplay = 0;

    let slot = 0;
    for (const wave of state.waves) {
      // `isWaveVisible` and nothing else. A wave scheduled early sits in
      // `Pending` — on the books, not on screen — and is skipped rather than
      // written with zero life: an unseen wave should cost an instance slot no
      // more than it costs a pixel.
      //
      // The same function answers the hit test, so "drawn" and "tappable" are
      // one decision rather than two that have to be kept in agreement.
      if (!isWaveVisible(wave)) continue;

      // The one branch styles introduce. A wave is drawn by exactly one path:
      // the mandala shader as before, or the gameplay field. Nothing here
      // touches the wave, its timing or its hit test — the same waves are
      // visible, in the same places, whichever way they are drawn.
      if (gameplay !== null) {
        const style = this.styleResolver(wave);
        if (style !== 'MANDALA') {
          const pigment = this.pigmentResolver?.(wave);
          const written = pigment === undefined
            ? gameplay.write(style, this.projectGameplay(wave))
            : gameplay.write(style, this.projectGameplay(wave), pigment);
          if (!written) this.droppedGameplay += 1;
          continue;
        }
      }

      if (slot >= this.field.capacity) break;
      this.write(slot, wave);
      slot += 1;
    }

    // Only clear as far as last frame reached; wiping the whole buffer every
    // frame would cost more than the waves themselves.
    if (slot < this.lastWritten) {
      this.scratch.life = 0;
      for (let index = slot; index < this.lastWritten; index += 1) {
        this.field.writeChartInstance(index, this.scratch);
      }
    }
    this.lastWritten = slot;
    // Draw only the slots in use, so vertex cost tracks live waves rather than
    // pool capacity.
    this.field.setChartInstanceCount(slot);
    this.field.commitChartInstances();
    gameplay?.commit();
  }

  /**
   * A read-only view of one wave, for a style renderer.
   *
   * Everything is derived; nothing is invented and nothing is written back. The
   * scratch record is reused, so a renderer must consume it before the next
   * call — which the field does, by copying the numbers it wants into its
   * instance buffer immediately.
   */
  private projectGameplay(wave: Wave): GameplayWaveState {
    const out = this.gameplayScratch;
    out.id = wave.id;
    out.eventIndex = wave.eventIndex;
    out.spawnTime = wave.visualSpawnTime;
    out.x = wave.originX;
    out.y = wave.originY;
    out.direction = wave.gateAngle * DEGREES_TO_RADIANS;
    out.gateHalfWidth = Math.max(0, wave.gateWidth) * 0.5 * DEGREES_TO_RADIANS;

    // The same normalisation the timing arc uses, so a style's growth and the
    // arc agree about how far along the wave is.
    const approach = Math.max(0.05, wave.bestTime - wave.visualSpawnTime);
    out.progress = (this.songTime - wave.visualSpawnTime) / approach;

    out.distance = wave.radius;
    out.despawnDistance = wave.despawnRadius;
    out.strength = clamp01(wave.intensity);
    out.lifetime = wave.age;
    out.seed = gameplayWaveSeed(wave.eventIndex);

    const warning = wave.state === WaveState.Warning;
    out.warning = warning;
    if (warning) {
      out.life = this.warningLife;
    } else {
      const fadeStart = wave.despawnRadius * (1 - this.fadeFraction);
      out.life =
        wave.radius <= fadeStart
          ? 1
          : Math.max(0, 1 - (wave.radius - fadeStart) / (wave.despawnRadius - fadeStart));
    }
    out.life *= 0.65 + 0.35 * clamp01(wave.intensity);

    out.outcome = outcomeOf(wave);
    if (wave.judgement === null) {
      out.resolve = 0;
      const sinceBad = this.songTime - wave.lastBadAt;
      out.badFlash =
        sinceBad >= 0 && sinceBad < this.badFlashSeconds
          ? 1 - sinceBad / this.badFlashSeconds
          : 0;
    } else {
      const fade = wave.fadeSeconds > 0 ? wave.fadeSeconds : this.resolveFadeSeconds;
      out.resolve = clamp01((this.songTime - wave.resolvedAt) / fade);
      out.badFlash = 0;
    }

    out.red = wave.dangerColor[0] ?? 1;
    out.green = wave.dangerColor[1] ?? 1;
    out.blue = wave.dangerColor[2] ?? 1;
    return out;
  }

  /** Blanks every slot, e.g. when a song is unloaded. */
  clear(): void {
    this.field.hideChartInstancesFrom(0);
    this.field.setChartInstanceCount(0);
    this.field.commitChartInstances();
    this.lastWritten = 0;
  }

  private write(slot: number, wave: Wave): void {
    const data = this.scratch;

    // The chart stores a full gate width in degrees; the shader wants a half
    // width in radians, because it compares against a shortest angular distance.
    data.gateHalfWidth = Math.max(0, wave.gateWidth) * 0.5 * DEGREES_TO_RADIANS;
    data.gateAngle = wave.gateAngle * DEGREES_TO_RADIANS;
    data.centerX = wave.originX;
    data.centerY = wave.originY;
    data.radius = wave.radius;

    const warning = wave.state === WaveState.Warning;
    data.state = warning ? 0 : 1;

    let life: number;
    if (warning) {
      // Radius is zero here by design, so the wave shows as a small forming seal
      // at its origin — the requirement document's advance notice of where it
      // will open.
      life = this.warningLife;
    } else {
      const fadeStart = wave.despawnRadius * (1 - this.fadeFraction);
      life =
        wave.radius <= fadeStart
          ? 1
          : Math.max(0, 1 - (wave.radius - fadeStart) / (wave.despawnRadius - fadeStart));
    }
    // Intensity is authored per event by the chart generator from the band
    // energy at that moment, so it modulates brightness rather than being unused.
    data.life = life * (0.65 + 0.35 * clamp01(wave.intensity));

    data.red = wave.dangerColor[0] ?? 1;
    data.green = wave.dangerColor[1] ?? 1;
    data.blue = wave.dangerColor[2] ?? 1;

    // Unity treats an all-zero Color as "unset" and substitutes a theme colour at
    // use time. The songpack layer deliberately preserves the zero rather than
    // inventing one, so the substitution belongs here.
    const gateR = wave.gateColor[0] ?? 0;
    const gateG = wave.gateColor[1] ?? 0;
    const gateB = wave.gateColor[2] ?? 0;
    const gateUnset = gateR + gateG + gateB <= 1e-5;
    data.gateRed = gateUnset ? DEFAULT_GATE_COLOR.r : gateR;
    data.gateGreen = gateUnset ? DEFAULT_GATE_COLOR.g : gateG;
    data.gateBlue = gateUnset ? DEFAULT_GATE_COLOR.b : gateB;

    // The note is presentation only for now: it selects a word slot and how
    // brightly that slot is drawn. Nothing here is read back by the simulation.
    const words = this.field.wordsPerRing;
    const chosen = this.noteWordSelector(wave, words);
    data.noteWordIndex = Number.isFinite(chosen) && chosen >= 0 ? Math.floor(chosen) % words : -1;
    data.noteRed = this.noteColor.r;
    data.noteGreen = this.noteColor.g;
    data.noteBlue = this.noteColor.b;
    data.noteGain = this.noteGain;
    // Peaks exactly at the best moment rather than breathing on its own clock.
    // One of the six words swelling as the moment arrives is a cue that reads
    // even when the arc is lost among two dozen overlapping seals.
    const proximity = 1 - clamp01(Math.abs(this.songTime - wave.bestTime) / this.notePulseWindow);
    data.notePulse = this.notePulseDepth * proximity * proximity;

    this.applyRotation(data, wave);
    this.applyTiming(data, wave);
    this.field.writeChartInstance(slot, data);
  }

  /**
   * Writes the timing arc and, if the wave has been judged, its dissolve.
   *
   * The arc's job is to make `bestTime` visible before it arrives. It is not a
   * decorative spin: the angle is derived so that the arc reaches the reference
   * position — straight up — at exactly `bestTime`, and keeps going afterwards.
   * A player who learns "press when it reaches the top" has learned the timing,
   * which is the whole point of drawing it.
   *
   * Direction and the starting side come from the event index, so the same
   * event always sweeps the same way.
   */
  private applyTiming(data: ChartInstanceData, wave: Wave): void {
    // Frozen at the judgement. Everything downstream — the timeline colour, the
    // note's position, the arc — is a function of this offset, so freezing the
    // one value stops all of them together. A resolving seal that kept sliding
    // down the palette would be reporting a timing the player can no longer act
    // on, and reporting it while dissolving.
    const offset =
      wave.judgement === null
        ? this.songTime - wave.bestTime
        : wave.resolvedAt - wave.bestTime;
    data.timingOffset = offset;
    // Handed over rather than recomputed: the note's convergence and the
    // simulation's idea of when the wave appeared have to be the same number.
    data.visualLead = wave.visualLeadTime;

    const seed = hash(wave.eventIndex ^ 0x5bf03635);
    const direction = (seed & 1) === 0 ? 1 : -1;
    // Normalised approach: 0 when the wave appeared, 1 at its best moment.
    // Measured from `visualSpawnTime`, so the sweep is exactly as long as the
    // warning the player actually got — which is what the wave speed setting
    // changes, and the only way the arc can stay honest across settings.
    const approach = Math.max(0.05, wave.bestTime - wave.visualSpawnTime);
    const progress = (this.songTime - wave.visualSpawnTime) / approach;
    // At progress 1 the term vanishes and the arc sits at the reference angle,
    // whichever way it travelled to get there.
    data.arcAngle = ARC_REFERENCE_ANGLE + direction * (1 - progress) * this.arcSweep;

    const judgement = wave.judgement;
    if (judgement === null) {
      data.resolveProgress = 0;
      data.gradeCode = -1;
      // A failed tap does not remove the wave, so its only feedback is this: a
      // short glitch on the seal itself, decaying over the cooldown so the
      // moment it becomes tappable again is the moment it looks settled.
      const sinceBad = this.songTime - wave.lastBadAt;
      data.badFlash =
        sinceBad >= 0 && sinceBad < this.badFlashSeconds
          ? 1 - sinceBad / this.badFlashSeconds
          : 0;
      return;
    }
    // Each grade gets its own dissolve length, so the fade the view draws and
    // the moment the simulation releases the slot are the same duration.
    const fade = wave.fadeSeconds > 0 ? wave.fadeSeconds : this.resolveFadeSeconds;
    data.resolveProgress = clamp01((this.songTime - wave.resolvedAt) / fade);
    data.gradeCode = gradeCode(judgement.grade);
    data.badFlash = 0;
  }

  /**
   * Turns every ring of the seal, derived entirely from `eventIndex`.
   *
   * Two properties are being bought here. The first is that replaying a chart
   * reproduces the identical set of seals — direction, rate and inscription all
   * hash from the event, never from wall time or spawn order. The second is that
   * the motion reads as *drifting* rather than as a motor: a slow base rate plus
   * a very low-frequency wobble, with the wobble's peak angular velocity kept an
   * order of magnitude under the base rate so no ring ever reverses.
   *
   * Each ring takes its own rate from its own slice of the hash, inside the
   * window the layout table gives it, and its sign from the table times the
   * wave's own direction. So neighbouring rings always oppose each other, no
   * two rings ever share a rate, and flipping one wave's direction flips the
   * whole mechanism rather than desynchronising it.
   *
   * The accumulated angles are computed here rather than in the shader so the
   * wobble can be an arbitrary function of the wave's own age.
   */
  private applyRotation(data: ChartInstanceData, wave: Wave): void {
    const seed = hash(wave.eventIndex);
    const direction = (seed & 1) === 0 ? 1 : -1;

    // Period of roughly one to two minutes: felt, not seen.
    const wobbleFrequency = 0.05 + unit(seed >>> 13) * 0.07;
    const wobbleAmplitude = 0.06 + unit(seed >>> 17) * 0.10;
    const basePhase = unit(seed >>> 23) * Math.PI * 2;
    const age = wave.age;

    const rings = this.field.textRings;
    for (let index = 0; index < rings.length; index += 1) {
      const ring = rings[index] as (typeof rings)[number];
      // A different shift per ring, so the rates are independent draws rather
      // than one rate scaled six ways — which would read as a rigid gear train.
      const rate =
        direction *
        ring.direction *
        (ring.minSpeed + unit(seed >>> (2 + index * 3)) * (ring.maxSpeed - ring.minSpeed));
      const phase = basePhase + index * 1.31;
      const amplitude = wobbleAmplitude * (index % 2 === 0 ? 1 : 0.7);
      const angle =
        rate * age + amplitude * Math.sin(age * wobbleFrequency * (1 + index * 0.19) + phase);
      this.spins[index] = angle;
    }

    data.spinRing0 = this.spins[0] as number;
    data.spinRing1 = this.spins[1] as number;
    data.spinRing2 = this.spins[2] as number;
    // The note ring and the innermost ornament ride the two angles the seal
    // already carried, which is why six rings needed only one new attribute.
    data.spinOuter = this.spins[3] as number;
    data.spinRing4 = this.spins[4] as number;
    data.spinInner = this.spins[5] as number;

    data.pattern = seed % this.field.patternCount;
  }
}

/**
 * Coarsens a judgement into what a renderer is allowed to know.
 *
 * PERFECT, GREAT and GOOD are all "hit" here; BAD is "safe" — the wave survived
 * a mistimed tap; MISS is "miss". A style must not be able to show the player
 * their exact grade, because the judgement panel is where that belongs and two
 * sources for one fact will eventually disagree.
 */
function outcomeOf(wave: Wave): GameplayWaveOutcome {
  const judgement = wave.judgement;
  if (judgement === null) return 'pending';
  switch (judgement.grade) {
    case 'miss':
      return 'miss';
    case 'bad':
      return 'safe';
    default:
      return 'hit';
  }
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Small integer hash, so one event index yields several independent choices. */
function hash(value: number): number {
  let x = (value + 0x9e3779b9) | 0;
  x = Math.imul(x ^ (x >>> 16), 0x85ebca6b);
  x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35);
  x ^= x >>> 16;
  return x >>> 0;
}

/** Maps the low bits of a hash into `[0, 1)`. */
function unit(value: number): number {
  return (value >>> 0) % 65536 / 65536;
}
